import React, { useState } from "react";
import { Card, Button, Space, Tag, message, Popconfirm } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useGetRules, useDeleteRule } from "~/services/template";
import { PreferredCommunicationMethod } from "~/utils/constants";
import { ModalRule } from "../modal-notifications";
import { TRule } from "~/types/rule";
import { useQueryClient } from "@tanstack/react-query";
import { TEMPLATE_KEYS } from "~/services/template/template.keys";

const NotificationRules: React.FC = () => {
  const [openRuleModal, setOpenRuleModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState<TRule | undefined>(undefined);
  const { data: rules, isLoading } = useGetRules();
  const deleteRuleMutation = useDeleteRule();
  const queryClient = useQueryClient();

  const handleEditRule = (rule: TRule) => {
    setSelectedRule(rule);
    setOpenRuleModal(true);
  };

  const handleDeleteRule = async (ruleId: string) => {
    try {
      await deleteRuleMutation.mutateAsync(ruleId);
      message.success("Rule deleted successfully");
      queryClient.invalidateQueries({ queryKey: TEMPLATE_KEYS.GET_RULES() });
    } catch (error) {
      message.error("Failed to delete rule");
      console.error("Error deleting rule:", error);
    }
  };

  const handleCloseModal = () => {
    setOpenRuleModal(false);
    setSelectedRule(undefined);
  };

  return (
    <Card
      loading={isLoading}
      title="Automated Notification Rules"
      style={{ height: "100%" }}
    >
      <div style={{ marginBottom: 16 }}>
        {rules?.items.map((rule, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 0",
              borderBottom:
                index < rules.items.length - 1 ? "1px solid #f0f0f0" : "none",
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500 }}>{rule.name}</div>
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                {rule.description}
              </div>
            </div>
            <Space>
              {rule.channels === PreferredCommunicationMethod.EMAIL ? (
                <Tag color="blue">Email</Tag>
              ) : rule.channels ===
                PreferredCommunicationMethod.TEXT_MESSAGE ? (
                <Tag color="purple">SMS</Tag>
              ) : (
                <>
                  <Tag color="blue">Email</Tag>
                  <Tag color="purple">SMS</Tag>
                </>
              )}
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditRule(rule)}
              />
              <Popconfirm
                title="Delete Rule"
                description="Are you sure you want to delete this rule?"
                onConfirm={() => handleDeleteRule(rule.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleteRuleMutation.isPending}
                />
              </Popconfirm>
            </Space>
          </div>
        ))}
      </div>
      <Button
        block
        icon={<PlusOutlined />}
        onClick={() => {
          setSelectedRule(undefined);
          setOpenRuleModal(true);
        }}
      >
        Create New Rule
      </Button>
      <ModalRule
        open={openRuleModal}
        onClose={handleCloseModal}
        rule={selectedRule}
      />
    </Card>
  );
};

export default NotificationRules;
